// 051 (d) Controles prenatales (5 renglones) y postparto (8 renglones): un Observation por renglón con dato, bajo su panel.
// La altura uterina de cada control ya tiene su propio Observation LOINC 11881-0 (`fhir051.ts`); aquí se adopta como miembro del
// panel y NO se repite como component. El renglón vacío no genera nada; un renglón que solo trae altura uterina tampoco genera
// el Observation del renglón (su dato ya está en el de altura). `iniciales` (iniciales del profesional) es administrativo: no se guarda.
import type { Observation } from '@medplum/fhirtypes';
import { NUM_CONTROLES_POSTPARTO, NUM_CONTROLES_PRENATALES, type EstadoControlesPrenatales051, type EstadoPostparto051 } from '../bloques051ControlesDatos';
import { cEntero, cFecha, cHora, cNumero, cSiNo, cTension, cTexto, esFechaIso, LOINC_051, local, Seccion051, sinVacios, U, type Contexto051 } from './base';

const PC = 'formularios.f051.controlPrenatal';
const PP = 'formularios.f051.postparto';

/** Panel de controles prenatales. `alturasUterinas`: `fullUrl` de los Observation de altura uterina ya creados en el lote, para colgarlos del panel. */
export function agregarControlesPrenatales(ctx: Contexto051, e: EstadoControlesPrenatales051, alturasUterinas: readonly string[] = []): void {
  const { t } = ctx;
  const s = new Seccion051(ctx, local('controles-prenatales', 'Controles prenatales'));
  for (const url of alturasUterinas) s.adoptar(url);
  e.filas.slice(0, NUM_CONTROLES_PRENATALES).forEach((f, i) => {
    const comps = sinVacios([
      cFecha(local('fecha-control', t(`${PC}.fecha`)), f.fecha),
      // La altura uterina numérica ya es su propio Observation (LOINC 11881-0); una no numérica ("24 cm") se conserva aquí como texto.
      Number.isNaN(Number(f.alturaUterina.trim().replace(',', '.'))) ? cTexto(local('altura-uterina-texto', t(`${PC}.alturaUterina`)), f.alturaUterina) : undefined,
      cNumero(LOINC_051.edadGestacional, f.edadGestacional, U.semanas),
      cNumero(LOINC_051.peso, f.peso, U.kg),
      cSiNo(local('ganancia-adecuada-peso', t(`${PC}.gananciaAdecuadaPeso`)), f.gananciaAdecuadaPeso),
      cTension(f.ta),
      cNumero(LOINC_051.pulso, f.fc, U.porMinuto),
      cNumero(LOINC_051.fr, f.fr, U.porMinuto),
      cNumero(LOINC_051.temperatura, f.temperatura, U.celsius),
      cNumero(LOINC_051.saturacion, f.saturacion, U.porcentaje),
      cTexto(local('proteinuria', t(`${PC}.proteinuria`)), f.proteinuria),
      cTexto(local('est-conc', t(`${PC}.estConc`)), f.estConc),
      cTexto(local('presentacion', t(`${PC}.presentacion`)), f.presentacion),
      cNumero(LOINC_051.fcf, f.frecuenciaCardiacaFetal, U.porMinuto),
      cTexto(local('movimientos-fetales', t(`${PC}.movimientosFetales`)), f.movimientosFetales),
      cSiNo(local('sangrado-vaginal', t(`${PC}.sangradoVaginal`)), f.sangradoVaginal),
      cTexto(local('examenes-tratamientos-score-mama', t(`${PC}.examenesTratamientosScoreMama`)), f.examenesTratamientosScoreMama),
      cFecha(local('proxima-cita', t(`${PC}.proximaCita`)), f.proximaCita),
    ]);
    if (comps.length === 0) return;
    // El número de renglón del papel se conserva aunque haya renglones vacíos en medio.
    const extra: Partial<Observation> = esFechaIso(f.fecha) ? { effectiveDateTime: f.fecha.trim() } : {};
    s.grupo(local('control-prenatal', t(`${PC}.filaTitulo`)), [cEntero(local('renglon', 'Renglón del papel'), String(i + 1)), ...comps], extra);
  });
  s.cerrar();
}

export function agregarPostparto(ctx: Contexto051, e: EstadoPostparto051): void {
  const { t } = ctx;
  const s = new Seccion051(ctx, local('postparto', 'Postparto'));
  e.filas.slice(0, NUM_CONTROLES_POSTPARTO).forEach((f, i) => {
    const comps = sinVacios([
      cEntero(local('dia-postparto', t(`${PP}.dia`)), f.dia),
      cHora(local('hora-control', t(`${PP}.hora`)), f.hora),
      cNumero(LOINC_051.temperatura, f.temperatura, U.celsius),
      cTension(f.ta),
      cNumero(LOINC_051.fr, f.fr, U.porMinuto),
      cNumero(LOINC_051.pulso, f.pulso, U.porMinuto),
      cNumero(LOINC_051.saturacion, f.saturacion, U.porcentaje),
      cTexto(local('est-conc', t(`${PP}.estConc`)), f.estConc),
      cTexto(local('involucion-uterina', t(`${PP}.involucionUterina`)), f.involucionUterina),
      cTexto(local('loquios', t(`${PP}.loquios`)), f.loquios),
      cTexto(local('score-mama', t(`${PP}.scoreMama`)), f.scoreMama),
    ]);
    if (comps.length === 0) return;
    s.grupo(local('control-postparto', t(`${PP}.filaTitulo`)), [cEntero(local('renglon', 'Renglón del papel'), String(i + 1)), ...comps]);
  });
  s.cerrar();
}
